import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, Download, Trash2, X } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';

export const ImageViewer = ({
  open,
  item,
  items = [],
  onClose,
  onPrevious,
  onNext,
  onDownload,
  onDelete
}) => {
  const { t, formatDate } = useI18n();
  const closeRef = useRef(null);
  const lastFocusRef = useRef(null);
  const index = item ? items.findIndex((entry) => entry.messageId === item.messageId && entry.url === item.url) : -1;
  const hasPrev = index > 0;
  const hasNext = index >= 0 && index < items.length - 1;

  useEffect(() => {
    if (!open) return undefined;
    lastFocusRef.current = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const timer = window.setTimeout(() => closeRef.current?.focus(), 0);

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose?.();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        onPrevious?.();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        onNext?.();
      } else if (event.key === 'Tab') {
        const dialog = event.currentTarget === window ? document.getElementById('image-viewer-dialog') : null;
        const root = dialog || document.getElementById('image-viewer-dialog');
        if (!root) return;
        const focusable = root.querySelectorAll('button:not([disabled])');
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      if (lastFocusRef.current instanceof HTMLElement) {
        lastFocusRef.current.focus();
      }
    };
  }, [open, onClose, onNext, onPrevious]);

  if (!open || !item || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div
      id="image-viewer-dialog"
      className="fixed inset-0 z-[60] flex flex-col bg-neutral-950/95 text-white"
      role="dialog"
      aria-modal="true"
      aria-label={t('imageView')}
    >
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 hover:bg-white/20"
          aria-label={t('imageCloseViewer')}
        >
          <X size={18} aria-hidden />
        </button>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onDownload?.(item)}
            className="inline-flex h-10 items-center gap-2 rounded-full bg-white/10 px-3 text-sm hover:bg-white/20"
            aria-label={t('imageDownload')}
          >
            <Download size={16} aria-hidden />
            <span className="hidden sm:inline">{t('imageDownload')}</span>
          </button>
          <button
            type="button"
            onClick={() => onDelete?.(item)}
            className="inline-flex h-10 items-center gap-2 rounded-full bg-white/10 px-3 text-sm text-rose-200 hover:bg-rose-500/30"
            aria-label={t('imageDelete')}
          >
            <Trash2 size={16} aria-hidden />
            <span className="hidden sm:inline">{t('imageDelete')}</span>
          </button>
        </div>
      </div>

      <div className="relative flex min-h-0 flex-1 items-center justify-center px-4">
        {hasPrev ? (
          <button
            type="button"
            onClick={onPrevious}
            className="absolute left-3 z-10 inline-flex h-11 w-11 items-center justify-center rounded-full bg-white/10 hover:bg-white/20"
            aria-label={t('imagePrevious')}
          >
            <ChevronLeft size={22} aria-hidden />
          </button>
        ) : null}
        <img
          src={item.url}
          alt={item.prompt || t('imageSection')}
          className="max-h-full max-w-full object-contain"
        />
        {hasNext ? (
          <button
            type="button"
            onClick={onNext}
            className="absolute right-3 z-10 inline-flex h-11 w-11 items-center justify-center rounded-full bg-white/10 hover:bg-white/20"
            aria-label={t('imageNext')}
          >
            <ChevronRight size={22} aria-hidden />
          </button>
        ) : null}
      </div>

      <div className="space-y-1 px-5 py-4 text-sm text-white/80">
        {item.prompt ? (
          <p>
            <span className="font-medium text-white">{t('imagePromptLabel')}: </span>
            {item.prompt}
          </p>
        ) : null}
        {item.model ? (
          <p>
            <span className="font-medium text-white">{t('imageModelLabel')}: </span>
            {item.model}
          </p>
        ) : null}
        {item.createdAt ? (
          <p>
            <span className="font-medium text-white">{t('imageCreatedAt')}: </span>
            {formatDate(item.createdAt, { dateStyle: 'medium', timeStyle: 'short' })}
          </p>
        ) : null}
      </div>
    </div>,
    document.body
  );
};
